import { array, bool, func, number, string } from 'prop-types'
import {
   useCallback,
   useEffect,
   useLayoutEffect,
   useMemo,
   useRef,
   useState,
} from 'react'
import HeaderRow from './HeaderRow'
import Loading from './Loading'
import NoData from './NoData'
import { StyledTable } from './StyledComponents'
import TableBodyRow from './TableBodyRow'

const arraysEqual = (a = [], b = []) =>
   a.length === b.length && a.every((val, i) => val === b[i])

const Table = ({
   data = [],
   emptyMessage = '',
   headers = [],
   isLoading = false,
   onClickRow,
   stickyColumnCount = 0,
   withPadding = true,
}) => {
   const memoizedData = useMemo(
      () => (Array.isArray(data) ? data : []),
      [data],
   )
   const memoizedHeaders = useMemo(
      () => (Array.isArray(headers) ? headers : []),
      [headers],
   )
   const hasData = useMemo(() => memoizedData?.length > 0, [memoizedData])
   const colSpan = useMemo(() => headers?.length, [headers])

   const effectiveStickyCount = useMemo(
      () => Math.max(0, Math.min(stickyColumnCount, memoizedHeaders.length)),
      [stickyColumnCount, memoizedHeaders.length],
   )

   // outerRef - vertikal scroll (StyledTable)
   // scrollRef - gorizontal scroll (.table-scroll-x)
   // scrollbarRef - pastdagi proxy scrollbar
   const outerRef = useRef(null)
   const scrollRef = useRef(null)
   const scrollbarRef = useRef(null)
   const headerRefs = useRef([])
   const isSyncingRef = useRef(false)
   const rafRef = useRef(null)

   const [scrollWidth, setScrollWidth] = useState(0)
   const [showScrollbar, setShowScrollbar] = useState(false)
   const [stickyOffsets, setStickyOffsets] = useState([])

   const setHeaderRef = useCallback((el, index) => {
      headerRefs.current[index] = el
   }, [])

   const measure = useCallback(() => {
      const el = scrollRef.current
      if (!el) return

      const nextScrollWidth = el.scrollWidth
      const nextShowScrollbar = el.scrollWidth - el.clientWidth > 1

      setScrollWidth(prev => (prev !== nextScrollWidth ? nextScrollWidth : prev))
      setShowScrollbar(prev =>
         prev !== nextShowScrollbar ? nextShowScrollbar : prev,
      )

      const offsets = []
      let cumulative = 0
      for (let i = 0; i < effectiveStickyCount; i += 1) {
         offsets[i] = cumulative
         const node = headerRefs.current[i]
         cumulative += node ? node.offsetWidth : 0
      }

      setStickyOffsets(prev => (arraysEqual(prev, offsets) ? prev : offsets))
   }, [effectiveStickyCount])

   const scheduleMeasure = useCallback(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(measure)
   }, [measure])

   useLayoutEffect(() => {
      scheduleMeasure()
   }, [scheduleMeasure, memoizedData.length, memoizedHeaders.length, isLoading])

   useEffect(() => {
      const el = scrollRef.current
      if (!el || typeof ResizeObserver === 'undefined') return undefined

      const resizeObserver = new ResizeObserver(() => scheduleMeasure())
      resizeObserver.observe(el)
      headerRefs.current.forEach(node => {
         if (node) resizeObserver.observe(node)
      })
      window.addEventListener('resize', scheduleMeasure)

      return () => {
         resizeObserver.disconnect()
         window.removeEventListener('resize', scheduleMeasure)
         if (rafRef.current) cancelAnimationFrame(rafRef.current)
      }
   }, [scheduleMeasure, effectiveStickyCount, memoizedHeaders.length])

   const handleTableScroll = useCallback(() => {
      if (isSyncingRef.current) {
         isSyncingRef.current = false
         return
      }
      if (scrollbarRef.current && scrollRef.current) {
         isSyncingRef.current = true
         scrollbarRef.current.scrollLeft = scrollRef.current.scrollLeft
      }
   }, [])

   const handleProxyScroll = useCallback(() => {
      if (isSyncingRef.current) {
         isSyncingRef.current = false
         return
      }
      if (scrollbarRef.current && scrollRef.current) {
         isSyncingRef.current = true
         scrollRef.current.scrollLeft = scrollbarRef.current.scrollLeft
      }
   }, [])

   return (
      <StyledTable ref={outerRef}>
         <div
            className="table-scroll-x"
            ref={scrollRef}
            onScroll={handleTableScroll}
         >
            <table>
               <thead>
                  <tr>
                     {memoizedHeaders.map((header, index) => {
                        const isSticky = index < effectiveStickyCount
                        return (
                           <HeaderRow
                              {...header}
                              withPadding={withPadding}
                              index={index}
                              key={index}
                              ref={
                                 isSticky
                                    ? el => setHeaderRef(el, index)
                                    : undefined
                              }
                              isSticky={isSticky}
                              isLastSticky={index === effectiveStickyCount - 1}
                              stickyLeft={
                                 isSticky ? stickyOffsets[index] ?? 0 : undefined
                              }
                           />
                        )
                     })}
                  </tr>
               </thead>
               <tbody>
                  {isLoading ? (
                     <Loading colSpan={colSpan} />
                  ) : hasData ? (
                     memoizedData.map((data, index) => (
                        <TableBodyRow
                           data={data}
                           headers={memoizedHeaders}
                           index={index}
                           key={index}
                           onClickRow={onClickRow}
                           withPadding={withPadding}
                           stickyColumnCount={effectiveStickyCount}
                           stickyOffsets={stickyOffsets}
                        />
                     ))
                  ) : (
                     <NoData colSpan={colSpan} emptyMessage={emptyMessage} />
                  )}
               </tbody>
            </table>
         </div>

         {showScrollbar && (
            <div
               className="sticky-hscrollbar"
               ref={scrollbarRef}
               onScroll={handleProxyScroll}
            >
               <div
                  className="sticky-hscrollbar-inner"
                  style={{ width: scrollWidth }}
               />
            </div>
         )}
      </StyledTable>
   )
}

Table.propTypes = {
   data: array,
   emptyMessage: string,
   headers: array,
   isLoading: bool,
   onClickRow: func,
   stickyColumnCount: number,
   withPadding: bool,
}

export default Table